import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Building2, AtSign, Trash2, BadgeCheck, RefreshCw, AlertTriangle } from "lucide-react";

const EMPTY_BANK = { nickname: "", type: "bank", account_holder_name: "", ifsc: "", account_number: "" };
const EMPTY_UPI = { nickname: "", type: "upi", upi_id: "" };

export default function Beneficiaries() {
  const [list, setList] = useState([]);
  const [limit, setLimit] = useState(5);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("bank");
  const [form, setForm] = useState(EMPTY_BANK);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/beneficiaries");
      // Backend now returns {items, count, limit}; keep backwards-compat with array
      if (Array.isArray(data)) {
        setList(data);
      } else {
        setList(data.items || []);
        setLimit(data.limit || 5);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load beneficiaries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const resetForm = (t) => {
    setForm(t === "bank" ? EMPTY_BANK : EMPTY_UPI);
    setTab(t);
  };

  const doSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = tab === "bank"
        ? { ...form, type: "bank" }
        : { nickname: form.nickname, type: "upi", upi_id: form.upi_id };
      await api.post("/beneficiaries", payload);
      toast.success("Beneficiary added");
      setOpen(false);
      resetForm("bank");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save beneficiary");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (id) => {
    try {
      await api.delete(`/beneficiaries/${id}`);
      toast.success("Beneficiary deleted");
      setList((l) => l.filter((b) => b.id !== id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const [syncing, setSyncing] = useState(null);
  const doSyncProvider = async (id) => {
    setSyncing(id);
    try {
      const { data } = await api.post(`/beneficiaries/${id}/refresh-provider-id`);
      if (data.recovered) {
        toast.success(data.message || "Beneficiary re-synced with provider");
      } else {
        toast.warning(data.message || "Provider could not return a bene_id. Contact support.");
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Re-sync failed");
    } finally {
      setSyncing(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Beneficiaries</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Your payout recipients
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            Add bank accounts for IMPS / NEFT / RTGS transfers or UPI handles for instant
            disbursement.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm("bank"); }}>
          <DialogTrigger asChild>
            <Button data-testid="open-add-beneficiary" className="group" disabled={list.length >= limit}>
              <Plus className="h-4 w-4 mr-1 transition-transform group-hover:rotate-90" strokeWidth={1.5} />
              Add beneficiary
              <span className="ml-2 text-[10px] font-mono opacity-70" data-testid="bene-count-badge">{list.length}/{limit}</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add beneficiary</DialogTitle>
              <DialogDescription>
                Verified instantly (mocked). Real flow uses penny-drop / UPI name match.
              </DialogDescription>
            </DialogHeader>
            <Tabs value={tab} onValueChange={(v) => resetForm(v)} data-testid="bene-type-tabs">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="bank" data-testid="tab-bank">Bank account</TabsTrigger>
                <TabsTrigger value="upi" data-testid="tab-upi">UPI ID</TabsTrigger>
              </TabsList>
              <form onSubmit={doSave} className="mt-4 space-y-4" data-testid="bene-form">
                <div>
                  <Label>Nickname</Label>
                  <Input data-testid="bene-nickname" value={form.nickname} onChange={onChange("nickname")} placeholder="e.g. John — primary" className="mt-1" />
                </div>
                <TabsContent value="bank" className="space-y-4 mt-0">
                  <div>
                    <Label>Account holder name</Label>
                    <Input data-testid="bene-holder" value={form.account_holder_name || ""} onChange={onChange("account_holder_name")} className="mt-1" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Account number</Label>
                      <Input data-testid="bene-account" value={form.account_number || ""} onChange={(e) => setForm({ ...form, account_number: e.target.value.replace(/\D/g, "") })} className="mt-1 font-mono" maxLength={20} />
                    </div>
                    <div>
                      <Label>IFSC</Label>
                      <Input data-testid="bene-ifsc" value={form.ifsc || ""} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} className="mt-1 font-mono" maxLength={11} placeholder="HDFC0001234" />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="upi" className="space-y-4 mt-0">
                  <div>
                    <Label>UPI ID</Label>
                    <Input data-testid="bene-upi-id" value={form.upi_id || ""} onChange={(e) => setForm({ ...form, upi_id: e.target.value.toLowerCase() })} className="mt-1 font-mono" placeholder="name@okhdfc" />
                  </div>
                </TabsContent>
                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="submit-beneficiary">
                    {saving ? "Saving…" : "Add beneficiary"}
                  </Button>
                </DialogFooter>
              </form>
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="beneficiary-list">
        {loading ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : list.length === 0 ? (
          <div className="col-span-full surface-card p-12 text-center">
            <Building2 className="h-6 w-6 mx-auto text-muted-foreground" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-muted-foreground">
              No beneficiaries yet. Add one to start sending payouts.
            </p>
          </div>
        ) : (
          list.map((b) => (
            <div key={b.id} data-testid={`bene-${b.id}`} className="surface-card p-5 interactive-card relative group">
              <div className="flex items-start justify-between">
                <div className={`h-10 w-10 rounded-md grid place-items-center ${b.type === "upi" ? "bg-secondary/15 text-secondary" : "bg-accent text-primary"}`}>
                  {b.type === "upi" ? <AtSign className="h-5 w-5" strokeWidth={1.5} /> : <Building2 className="h-5 w-5" strokeWidth={1.5} />}
                </div>
                {b.verified && <Badge className="bg-success text-success-foreground"><BadgeCheck className="h-3 w-3 mr-1" />Verified</Badge>}
              </div>
              <div className="font-semibold mt-3">{b.nickname}</div>
              {b.type === "upi" ? (
                <div className="font-mono text-sm mt-1 text-muted-foreground">{b.upi_id}</div>
              ) : (
                <>
                  <div className="text-sm mt-1">{b.account_holder_name}</div>
                  <div className="font-mono text-xs text-muted-foreground mt-1">
                    ••••{(b.account_number || "").slice(-4)} · {b.ifsc}
                  </div>
                </>
              )}
              {b.type === "bank" && !b.provider_bene_id && (
                <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-900 dark:text-amber-200">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" strokeWidth={1.5} />
                  <div className="flex-1">
                    <div className="font-medium">Provider sync pending</div>
                    <div className="opacity-80 mt-0.5">Payouts may fail until this beneficiary is re-synced.</div>
                    <button
                      type="button"
                      data-testid={`sync-bene-${b.id}`}
                      disabled={syncing === b.id}
                      onClick={() => doSyncProvider(b.id)}
                      className="mt-1.5 inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline disabled:opacity-60"
                    >
                      <RefreshCw className={`h-3 w-3 ${syncing === b.id ? "animate-spin" : ""}`} strokeWidth={2} />
                      {syncing === b.id ? "Syncing…" : "Sync with provider"}
                    </button>
                  </div>
                </div>
              )}
              <button
                type="button"
                data-testid={`delete-bene-${b.id}`}
                onClick={() => doDelete(b.id)}
                className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:bg-destructive/10 p-1.5 rounded-md"
                aria-label="Delete beneficiary"
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          ))
        )}
      </div>
    </DashboardLayout>
  );
}
